<script setup lang="ts" generic="T">
import type { Color } from './UiColorBubble.vue'

const t2 = useT2Base({
  Yes: 'Taip',
  No: 'Ne',
  Required: 'Privaloma',
})

const props = defineProps<{
  show?: boolean
  required?: boolean
  currentColor?: boolean
  color?: Color
  p?: 'never'
  price?: boolean
  end?: boolean
  col?: boolean
  mono?: boolean
  small?: boolean
  label: stringI18n | undefined
  mark?: boolean
  value: T
  desk?: boolean
  classValue?: string | (string | None)[] | None
  ui?(i: NN<T>): string | stringI18n
}>()

const hasValue = (i: T): i is NN<T> => i !== null && i !== undefined && i !== ''

function toUi(val: T) {
  if (hasValue(val) && props.ui) return props.ui(val)
  if (typeof val === 'boolean') return val ? t2('Yes') : t2('No')
  if (typeof val === 'object') return JSON.stringify(val, null, 2)
  return val
}
</script>

<template>
  <span
    v-if="show || required || hasValue(value)"
    :class="[
      $style.line,
      col ? '_col' : '_row _wrap',
      !ui && helpers.isObject(value) && '_c0 _bgRed',
      currentColor ? 'currentColor' : 'black',
      (small || col) && '_flexline',
      { small, mark, end, desk },
    ]"
  >
    <slot name="label" :label>{{ label }}</slot>

    <slot :em-class="$style.em2">
      <em v-if="required && !hasValue(value)" :class="[$style.em, classValue]"><UiMsg red :txt="t2('Required')" /></em>
      <b v-if="typeof value === 'string' && price" :class="[$style.em, classValue]" class="_mono _mark">{{ value }}<UiColorBubble v-if="color" :color class="_mta _mba _ml1" /></b>
      <b v-else-if="typeof value === 'number' && price" :class="[$style.em, classValue]" class="_mono _mark"
        >{{ helpers.priceFormat(value) }}<UiColorBubble v-if="color" :color class="_mta _mba _ml1"
      /></b>
      <em v-else-if="hasValue(value)" :class="[$style.em, mono ? '_mono' : '_fw-xl', classValue]">{{ toUi(value) }}<UiColorBubble v-if="color" :color class="_mta _mba _ml1" /></em>
    </slot>

    <slot name="after" />
  </span>
</template>

<style module>
.line {
  composes: global(_fs09);
  composes: global(_fw-l);
  composes: global(_ls-xs);
  composes: global(_hm22);
  composes: global(_ctrY);
  composes: global(_wm70ch);

  word-break: keep-all;
  line-height: 1.425;
  gap: 6px 10px;
}
.line:global(._col),
.line:global(.small) {
  align-items: flex-start;
  gap: 2px;
  color: hsl(240, 4.9%, 47.8%);
  line-height: 1;
}

.line:global(.currentColor) {
  color: currentColor;
}
.line:global(.black) {
  color: hsl(240, 10%, 17.5%);
}
.line:global(.small) {
  gap: 8px;
  min-height: 0;
}
.line:global(._col) {
  align-items: flex-start;
  gap: 1px;
}
.line:global(._col):global(.end) {
  align-items: flex-end;
}
.line:global(._col) .em {
  line-height: 1.425;
}

.line:global(.black) .em {
  color: var(--black);
}
.em {
  composes: global(_wm100p);
  composes: global(_ws_pre);
  composes: global(_break_words);
}
.em2 {
  composes: em;
  composes: global(_fw-xl);
}

.line:global(.mark) .em {
  font-weight: var(--fw-xl);
}

:global(.m) .line:global(._col),
:global(.m) .line:global(.small) {
  gap: 6px;
  color: var(--black);
  letter-spacing: var(--ls-xxs);
  font-weight: var(--fw-l);
}
:global(.m) .line:global(._col) em,
:global(.m) .line:global(.small) em {
  letter-spacing: var(--ls-xs);
}

.line:global(.desk) {
  font-weight: var(--fw-xl);
  gap: 2px;
}
.line:global(.desk) em {
  font-weight: var(--fw-l);
}
</style>
